import { createContext, useContext, useEffect } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useLocalForage } from "~served/ui-comps/hooks/use-local-forage";
import { SHARED_STORAGE_KEYS } from "~served/ui-comps/storage-keys";
import { useInitApp } from "~/hooks/use-init-app";
import { getHubGqlWsClient } from "~/libs/gql";
import { setRumStaff, setRumVenue, startRum } from "~/libs/rum";
import {
	clearSharedWsClient,
	setSharedWsClient,
} from "~/libs/ws-subscription-manager";
import { useAuthCache } from "~/queries/use-auth-cache";
import { subscribeToSystemHealth } from "~/queries/use-subscribe-to-system-health";
import { subscribeToVenueLocations } from "~/queries/use-subscribe-to-venue-locations";
import { subscribeToVenueNotifications } from "~/queries/use-subscribe-to-venue-notifications";
import { subscribeToVenueOrders } from "~/queries/use-subscribe-to-venue-orders";
import { useVenueId } from "~/store";
import { AppThemeProvider } from "~served/ui-comps/themes";

const ColorModeContext = createContext<() => void>(() => undefined);
export const useToggleColorMode = () => useContext(ColorModeContext);

export const AppProvider = ({ children }) => {
	useInitApp();
	useWsSubscriptions();
	useSessionTags();

	const [isDarkMode, setIsDarkMode] = useLocalForage(
		SHARED_STORAGE_KEYS.DARK_MODE,
		false,
	);

	const toggleColorMode = () => setIsDarkMode((prev) => !prev);

	return (
		<ColorModeContext.Provider value={toggleColorMode}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<AppThemeProvider mode={isDarkMode ? "dark" : "light"}>
					{children}
				</AppThemeProvider>
			</LocalizationProvider>
		</ColorModeContext.Provider>
	);
};

// Tags Clarity and OpenObserve RUM sessions with the venue and staff, so a
// session can be found by who used it and where.
const useSessionTags = () => {
	const venueId = useVenueId();
	const { data: auth } = useAuthCache();

	useEffect(() => {
		startRum();
	}, []);

	useEffect(() => {
		setRumVenue(venueId);
		if (!window.clarity) return;
		if (venueId) window.clarity("set", "venueId", venueId);
	}, [venueId]);

	useEffect(() => {
		setRumStaff(auth ?? undefined, venueId);
	}, [auth?._id, venueId]);

	useEffect(() => {
		if (!window.clarity) return;
		const staffId = auth?._id;
		if (staffId) window.clarity("set", "staffId", staffId);
	}, [auth?._id]);
};

const useWsSubscriptions = () => {
	const venueId = useVenueId();
	const { data: auth } = useAuthCache();
	const staffId = auth?._id;

	useEffect(() => {
		// Effect-local so a cleanup can only ever dispose its own client.
		const client = getHubGqlWsClient();
		setSharedWsClient(client);

		/**
		 * put all subscriptions here
		 * so that when params change, the client is re-initialized with all the subscriptions.
		 * The hub reads the staff identity once per connection, so a login or
		 * logout needs a new connection too.
		 */
		void subscribeToSystemHealth(client);
		if (venueId) {
			void subscribeToVenueLocations(client);
			void subscribeToVenueNotifications(client);
			void subscribeToVenueOrders(client);
		}

		return () => {
			clearSharedWsClient(client);
			client.dispose();
		};
	}, [venueId, staffId]);
};

declare global {
	interface Window {
		clarity?: (command: "set", key: string, value: string) => void;
	}
}
